import React, { useState, useEffect } from 'react';
import Pagination from './Pagination';
import { DownloadIcon } from './Icons';
import { downloadCsv } from './csvExport';
import { localDateStr } from './callUtils';

const DEFAULT_PAGE_SIZE = 50;

const SEARCHABLE_FIELDS = [
  'uuid',
  'call_to_number',
  'caller_id_number',
  'ivr_id',
  'ivr_name',
  'digit_pressed',
  'call_id',
  'billing_circle',
  'customer_no_with_prefix',
];

const EXPORT_COLUMNS = [
  { header: 'UUID', field: 'uuid' },
  { header: 'Call To Number', field: 'call_to_number' },
  { header: 'Caller ID', field: 'caller_id_number' },
  { header: 'Start Time', field: 'start_stamp' },
  { header: 'IVR ID', field: 'ivr_id' },
  { header: 'IVR Name', field: 'ivr_name' },
  { header: 'Digit Pressed', field: 'digit_pressed' },
  { header: 'Call ID', field: 'call_id' },
  { header: 'Billing Circle', field: 'billing_circle' },
  { header: 'Customer No', field: 'customer_no_with_prefix' },
];

function AllFieldsTable({ calls }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');

  const searchTerm = search.trim().toLowerCase();
  const filteredCalls = !searchTerm
    ? calls
    : calls.filter((call) =>
        SEARCHABLE_FIELDS.some((field) => (call[field] || '').toString().toLowerCase().includes(searchTerm))
      );

  const totalPages = Math.max(1, Math.ceil(filteredCalls.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const handleExportCsv = () => {
    const headers = EXPORT_COLUMNS.map((col) => col.header);
    const rows = filteredCalls.map((call) => EXPORT_COLUMNS.map((col) => call[col.field] || ''));
    downloadCsv(`all-fields-${localDateStr(new Date())}.csv`, headers, rows);
  };

  const pageCalls = filteredCalls.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <div className="incoming-filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search any field (number, UUID, IVR name, call ID...)"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search all fields"
        />
        <button type="button" className="btn btn-secondary export-csv-btn" onClick={handleExportCsv}>
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>UUID</th>
              <th>Call To Number</th>
              <th>Caller ID</th>
              <th>Start Time</th>
              <th>IVR ID</th>
              <th>IVR Name</th>
              <th>Digit Pressed</th>
              <th>Call ID</th>
              <th>Billing Circle</th>
              <th>Customer No</th>
            </tr>
          </thead>
          <tbody>
            {filteredCalls.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>
                  {calls.length === 0 ? 'No data received yet. Waiting for webhooks...' : 'No matching calls found.'}
                </td>
              </tr>
            ) : (
              pageCalls.map((call) => (
                <tr key={call.id}>
                  <td>{call.uuid || '-'}</td>
                  <td>{call.call_to_number || '-'}</td>
                  <td>{call.caller_id_number || '-'}</td>
                  <td>{call.start_stamp || '-'}</td>
                  <td>{call.ivr_id || '-'}</td>
                  <td>{call.ivr_name || '-'}</td>
                  <td>{call.digit_pressed || '-'}</td>
                  <td>{call.call_id || '-'}</td>
                  <td>{call.billing_circle || '-'}</td>
                  <td>{call.customer_no_with_prefix || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredCalls.length}
      />
    </>
  );
}

export default AllFieldsTable;
